import React, { useState } from 'react';
import styled from 'styled-components';

import { downloadPlayerReportPdf } from '../../pages/player/api';
import { InlinePlayerLoader } from '../PlayerLoader';
import { REPORTS_PHASE, useReportsPhase } from '../../helpers/reportsPhase';

const triggerBlobDownload = (data, filename) => {
  const blob = data instanceof Blob ? data : new Blob([data], { type: 'application/pdf' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => window.URL.revokeObjectURL(url), 2000);
};

// SMRTSTATS-312 — per-match PDF download, visible only to phase3 users
// (active main sub + Reports add-on). Scopes the season report to a single
// match via date_from/date_to. Hidden for phase1/phase2 — the sample-only
// affordance lives on the Dashboard via ViewFullReportLink.
const MatchReportButton = ({ seasonId, matchId, matchDate, competitionId, eligible }) => {
  const phase = useReportsPhase();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  if (phase !== REPORTS_PHASE.PHASE3) return null;
  if (!eligible) return null;
  if (!seasonId || !matchDate) return null;

  const onDownload = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const dateStr = String(matchDate).slice(0, 10);
      const competitionIds = competitionId != null ? [competitionId] : [];
      const res = await downloadPlayerReportPdf(seasonId, competitionIds, {
        date_from: dateStr,
        date_to: dateStr,
      });
      triggerBlobDownload(res.data, `player-report-match-${matchId}.pdf`);
    } catch (e) {
      let message = 'Could not generate report. Please try again.';
      if (e?.response?.data instanceof Blob) {
        try {
          const text = await e.response.data.text();
          const parsed = JSON.parse(text);
          if (parsed?.message) message = parsed.message;
        } catch {/* keep default */}
      } else if (e?.response?.data?.message) {
        message = e.response.data.message;
      }
      setError(message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Wrap>
      <DownloadButton type="button" onClick={onDownload} disabled={busy}>
        {busy ? (
          <>
            <InlinePlayerLoader />
            <span>Generating PDF…</span>
          </>
        ) : (
          <span>Download match report</span>
        )}
      </DownloadButton>
      {error && <ErrorText role="alert">{error}</ErrorText>}
    </Wrap>
  );
};

export default MatchReportButton;

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DownloadButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 16px;
  background: ${(props) => props.theme.row};
  color: ${(props) => props.theme.white};
  border: 1px solid ${(props) => props.theme.borderColor};
  border-radius: 8px;
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 14px;
  cursor: pointer;
  transition: border-color 120ms ease, background 120ms ease;

  &:hover:not(:disabled) { border-color: ${(props) => props.theme.green}; }
  &:disabled { cursor: default; opacity: 0.85; }
`;

const ErrorText = styled.div`
  font-size: 12px;
  color: ${(props) => props.theme.red};
  font-family: 'SatoshiMedium', sans-serif;
`;
